var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { hasValue, toJSON } from '@utils';
/** Unwrap the Lucy service response, which may be `[{status,data}]`, `{status,data}`, or the value itself. */
function unwrap(res) {
    const payload = Array.isArray(res) ? res[0] : res;
    return payload && typeof payload === 'object' && 'data' in payload ? payload.data : payload;
}
/**
 * Predefined queries authored in the Lucy Data Explorer for a given object type.
 * Returns [] when the object type is empty or the Lucy Query service is unavailable.
 */
export function getPredefinedQueries(context, objectType) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!hasValue(objectType))
            return [];
        try {
            const res = yield context.executeService('Lucy', 'Query:GetPaginatedQueries', { ObjectType: objectType, max: 100, last: 0, query: '' }, { json: true });
            const rows = toJSON(unwrap(res), []);
            return Array.isArray(rows) ? rows : [];
        }
        catch (e) {
            console.error('Unable to load predefined queries. Error: ', e);
            return [];
        }
    });
}
/**
 * Load a single predefined query's pipeline (the `query` inside its serialized `Configuration`).
 * Returns null when the query can't be loaded or has no pipeline.
 */
export function getPredefinedQueryPipeline(context, key) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (!hasValue(key))
            return null;
        try {
            const res = yield context.executeService('Lucy', 'Query:Details', { Key: key }, { json: true });
            const details = toJSON(unwrap(res), null);
            const config = toJSON(details === null || details === void 0 ? void 0 : details.Configuration, null);
            return (_a = config === null || config === void 0 ? void 0 : config.query) !== null && _a !== void 0 ? _a : null;
        }
        catch (e) {
            console.error('Unable to load predefined query pipeline. Error: ', e);
            return null;
        }
    });
}
/** Look up an active query by exact name — used to block duplicate names on save. */
export function findQueryByName(context, name) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!hasValue(name))
            return null;
        try {
            const res = yield context.executeService('Lucy', 'Query:FindQuery', { name }, { json: true });
            return toJSON(unwrap(res), null);
        }
        catch (e) {
            console.error('Unable to find predefined query by name. Error: ', e);
            return null;
        }
    });
}
/** Look up a query by its (slug) ID — used to resolve a saved-view reference to the live query. */
export function findQueryById(context, id) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!hasValue(id))
            return null;
        try {
            const res = yield context.executeService('Lucy', 'Query:FindQuery', { id }, { json: true });
            return toJSON(unwrap(res), null);
        }
        catch (e) {
            console.error('Unable to find predefined query by id. Error: ', e);
            return null;
        }
    });
}
/** Resolve a saved-view reference to the live query: its current name + pipeline (null if deleted). */
export function resolvePredefinedQueryById(context, id) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const found = yield findQueryById(context, id);
        if (!found)
            return null;
        const config = toJSON(found.Configuration, null);
        return { name: found.Name, pipeline: (_a = config === null || config === void 0 ? void 0 : config.query) !== null && _a !== void 0 ? _a : null };
    });
}
/** Slugify a name into a query ID (lowercase, hyphenated) with a unique token so IDs are never reused. */
function generateQueryId(name) {
    const slug = String(name).toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'query';
    return `${slug}-${Date.now().toString(36)}`;
}
/**
 * Create a new predefined Lucy query from a user-built pipeline.
 * Object types are the widget's configured type(s); `question` (the NL prompt, if any) and
 * `schema` are stored so the query carries enough metadata to reopen/refine later.
 * Returns the new { id, name } or null.
 */
export function createPredefinedQuery(context, params) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e;
        const name = String(params.name).trim();
        if (!hasValue(name))
            return null;
        const id = generateQueryId(name);
        const Configuration = JSON.stringify({
            id,
            name,
            description: (_a = params.description) !== null && _a !== void 0 ? _a : '',
            query: params.pipeline,
            schema: (_b = params.schema) !== null && _b !== void 0 ? _b : [],
            question: (_c = params.question) !== null && _c !== void 0 ? _c : '',
        });
        const res = yield context.executeService('Lucy', 'Query:Create', {
            ID: id,
            Name: name,
            Description: (_d = params.description) !== null && _d !== void 0 ? _d : '',
            ObjectTypes: ((_e = params.objectTypes) !== null && _e !== void 0 ? _e : []).join(','),
            Configuration,
        }, { json: true });
        const created = unwrap(res);
        // Surface a backend validation failure (e.g. duplicate name/ID) to the caller.
        if (created && typeof created === 'object' && 'errorMessage' in created && created.errorMessage) {
            throw new Error(String(created.errorMessage));
        }
        return { id, name };
    });
}
/**
 * Save a modified pipeline back into an existing predefined query (by reference ID),
 * preserving the query's name/description/question/object types. Updates the pipeline
 * (and schema, when provided).
 */
export function saveChangesToQuery(context, id, pipeline, schema) {
    return __awaiter(this, void 0, void 0, function* () {
        const found = yield findQueryById(context, id);
        if (!found)
            throw new Error('Query not found');
        const config = toJSON(found.Configuration, {}) || {};
        config.query = pipeline;
        if (schema !== undefined)
            config.schema = schema;
        yield context.executeService('Lucy', 'Query:UpdateConfiguration', { ID: id, Configuration: JSON.stringify(config) }, { json: true });
        return true;
    });
}
